import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { coachAPI, curriculumAPI, sceneAPI } from '../../services/api';
import { confirm, toast } from '../../../../shared/utils/overlays';
import CoachLayout from './CoachLayout';

/* ── Tutorial list view ──────────────────────────────────────────────────── */

function TutorialList({ onEdit, onNew }) {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    setLoading(true);
    const params = filter !== 'all' ? { status: filter } : {};
    coachAPI.listTutorials(params)
      .then(({ data }) => setTutorials(data.tutorials || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Delete tutorial?',
      message: 'Delete this lesson? This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await coachAPI.deleteTutorial(id).catch(() => {});
    toast.info('Tutorial deleted');
    load();
  };

  const handlePublish = async (id) => {
    await coachAPI.publishTutorial(id).catch(() => {});
    load();
  };

  return (
    <div>
      <div className="coach-section">
        <div className="coach-section-header">
          <h2 className="coach-section-title">Lessons</h2>
          <div className="flex-row">
            <select
              className="coach-select"
              style={{ width: 140 }}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="draft">Drafts</option>
              <option value="published">Published</option>
            </select>
            <button className="btn btn-primary" onClick={onNew}>+ New Lesson</button>
          </div>
        </div>

        {loading ? (
          <div className="coach-loading">Loading lessons…</div>
        ) : tutorials.length === 0 ? (
          <div className="coach-empty">
            <span className="coach-empty-icon">📝</span>
            <div className="coach-empty-title">No lessons yet</div>
            <div className="coach-empty-desc">Create your first lesson using the button above.</div>
          </div>
        ) : (
          <table className="coach-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Domain</th>
                <th>Difficulty</th>
                <th>Status</th>
                <th>Segments</th>
                <th>Tags</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tutorials.map((t) => (
                <tr key={t.tutorial_id}>
                  <td style={{ fontWeight: 600 }}>{t.title}</td>
                  <td className="text-muted text-sm">{t.domain_id}</td>
                  <td><span className={`badge badge-${t.difficulty}`}>{t.difficulty}</span></td>
                  <td><span className={`badge badge-${t.status}`}>{t.status}</span></td>
                  <td className="text-muted text-sm">{t.segments?.length ?? 0}</td>
                  <td className="text-muted text-sm">{(t.tags || []).join(', ') || '—'}</td>
                  <td>
                    <div className="flex-row">
                      <button className="btn btn-outline btn-sm" onClick={() => onEdit(t.tutorial_id)}>Edit</button>
                      {t.status === 'draft' && (
                        <button className="btn btn-primary btn-sm" onClick={() => handlePublish(t.tutorial_id)}>Publish</button>
                      )}
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(t.tutorial_id)}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ── Tutorial editor ─────────────────────────────────────────────────────── */

function TutorialEditor({ tutorialId, onBack }) {
  const navigate = useNavigate();
  const [tutorial, setTutorial] = useState(null);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  // New tutorial form
  const [newTutForm, setNewTutForm] = useState({
    domain_id: 'football',
    title: '',
    difficulty: 'beginner',
    tags: '',
  });

  // Segment form
  const [segForm, setSegForm] = useState({
    segment_type: 'concept',
    title: '',
    authoring_engine: 'native_video',
    concept_id: '',
    scenario_id: '',
    script_text: '',
    resource_url: '',
    source_name: '',
    estimated_minutes: '',
    // prompt_canvas fields
    topic_summary: '',
    difficulty: 'beginner',
    key_insight: '',
    saveable: true,
  });

  // Dynamic steps builder for prompt_canvas segments
  const [promptCanvasSteps, setPromptCanvasSteps] = useState([]);

  // Scene options for the segment picker — loaded lazily when the coach
  // wants to link an existing scene.
  const [sceneOptions, setSceneOptions] = useState([]);
  const [scenesLoading, setScenesLoading] = useState(false);
  const [linkExisting, setLinkExisting] = useState(false);
  // Per-domain scene-type catalog — drives the "Edit content →" link's
  // designer_route on each segment row.  Refetched whenever the
  // tutorial's domain changes.
  const [sceneTypes, setSceneTypes] = useState([]);

  useEffect(() => {
    curriculumAPI.listDomains()
      .then(({ data }) => setDomains(data.domains || []))
      .catch(() => {});

    if (tutorialId && tutorialId !== 'new') {
      coachAPI.getTutorial(tutorialId)
        .then(({ data }) => setTutorial(data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [tutorialId]);

  useEffect(() => {
    if (!tutorial?.domain_id) return;
    sceneAPI.listSceneTypes(tutorial.domain_id)
      .then(({ data }) => setSceneTypes(data?.scene_types || []))
      .catch(() => setSceneTypes([]));
  }, [tutorial?.domain_id]);

  const handleCreateTutorial = async () => {
    if (!newTutForm.title.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...newTutForm,
        tags: newTutForm.tags.split(',').map((t) => t.trim()).filter(Boolean),
      };
      const { data } = await coachAPI.createTutorial(payload);
      setTutorial(data);
      setSaveMsg('Lesson created!');
    } catch {
      setSaveMsg('Error creating lesson.');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 3000);
    }
  };

  const handleUpdateMeta = async () => {
    if (!tutorial) return;
    setSaving(true);
    try {
      const { data } = await coachAPI.updateTutorial(tutorial.tutorial_id, {
        title: tutorial.title,
        difficulty: tutorial.difficulty,
        tags: tutorial.tags,
      });
      setTutorial(data);
      setSaveMsg('Saved!');
    } catch {
      setSaveMsg('Save failed.');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 3000);
    }
  };

  const handleAddSegment = async () => {
    if (!segForm.title.trim() || !tutorial) return;
    if (segForm.segment_type === 'scenario' && !segForm.scenario_id) {
      setSaveMsg('Error: pick a scene from the library for this scenario segment.');
      setTimeout(() => setSaveMsg(null), 4000);
      return;
    }
    if (segForm.segment_type === 'curated_resource' && !segForm.resource_url.trim()) {
      setSaveMsg('Error: a Resource URL is required for curated resource segments.');
      setTimeout(() => setSaveMsg(null), 4000);
      return;
    }
    if (segForm.segment_type === 'prompt_canvas' && promptCanvasSteps.length === 0) {
      setSaveMsg('Error: at least one step is required for prompt canvas segments.');
      setTimeout(() => setSaveMsg(null), 4000);
      return;
    }
    setSaving(true);
    try {
      const isCuratedResource = segForm.segment_type === 'curated_resource';
      const isPromptCanvas    = segForm.segment_type === 'prompt_canvas';
      const payload = {
        segment_type: segForm.segment_type,
        title: segForm.title,
        authoring_engine: segForm.authoring_engine,
        concept_id: segForm.concept_id || null,
        scenario_id: segForm.segment_type === 'scenario' ? segForm.scenario_id : null,
        scene_id: (linkExisting && segForm.scenario_id) ? segForm.scenario_id : null,
        script_text: segForm.script_text || null,
        resource_url: isCuratedResource ? segForm.resource_url || null : null,
        source_name: isCuratedResource ? segForm.source_name || null : null,
        estimated_minutes: isCuratedResource && segForm.estimated_minutes
          ? Number(segForm.estimated_minutes)
          : null,
        // prompt_canvas inline data
        ...(isPromptCanvas ? {
          topic_summary: segForm.topic_summary || null,
          difficulty:    segForm.difficulty    || null,
          key_insight:   segForm.key_insight   || null,
          saveable:      segForm.saveable,
          steps:         promptCanvasSteps,
        } : {}),
      };
      const { data } = await coachAPI.addSegment(tutorial.tutorial_id, payload);
      setTutorial((prev) => ({ ...prev, segments: [...(prev.segments || []), data] }));
      setSegForm({
        segment_type: 'concept',
        title: '',
        authoring_engine: 'native_video',
        concept_id: '',
        scenario_id: '',
        script_text: '',
        resource_url: '',
        source_name: '',
        estimated_minutes: '',
        topic_summary: '',
        difficulty: 'beginner',
        key_insight: '',
        saveable: true,
      });
      setPromptCanvasSteps([]);
      setSaveMsg('Segment added!');
    } catch (err) {
      setSaveMsg(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 4000);
    }
  };

  // Load scene options on demand. Switches to "scenario" type → fetch
  // once. Filters to the tutorial's domain so the picker is useful and
  // small (a coach's library can run into the hundreds of scenes).
  const needsScenes = segForm.segment_type === 'scenario' || linkExisting;
  useEffect(() => {
    if (!needsScenes || sceneOptions.length > 0) return;
    setScenesLoading(true);
    sceneAPI
      .list({ domain_id: tutorial?.domain_id || 'football', limit: 500 })
      .then(({ data }) => setSceneOptions(data?.scenes || []))
      .catch(() => setSceneOptions([]))
      .finally(() => setScenesLoading(false));
  }, [needsScenes, tutorial, sceneOptions.length]);

  const handlePublish = async () => {
    setSaving(true);
    try {
      await coachAPI.publishTutorial(tutorial.tutorial_id);
      setTutorial((prev) => ({ ...prev, status: 'published' }));
      setSaveMsg('Published!');
    } catch {
      setSaveMsg('Publish failed.');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 3000);
    }
  };

  if (loading) return <div className="coach-loading">Loading…</div>;

  /* ── New tutorial form ─────────────────────────────────────────────────── */
  if (!tutorial) {
    return (
      <div className="coach-section">
        <div className="coach-section-header">
          <h2 className="coach-section-title">New Lesson</h2>
          <button className="btn btn-outline" onClick={onBack}>← Back to list</button>
        </div>
        <div style={{ maxWidth: 500 }}>
          <div className="coach-form-group">
            <label className="coach-label">Domain</label>
            <select
              className="coach-select"
              value={newTutForm.domain_id}
              onChange={(e) => setNewTutForm((f) => ({ ...f, domain_id: e.target.value }))}
            >
              {domains.map((d) => (
                <option key={d.domain_id} value={d.domain_id}>{d.display_name}</option>
              ))}
              {domains.length === 0 && <option value="football">Football</option>}
            </select>
          </div>
          <div className="coach-form-group">
            <label className="coach-label">Title</label>
            <input
              className="coach-input"
              value={newTutForm.title}
              onChange={(e) => setNewTutForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Reading Cover 2 Shells"
            />
          </div>
          <div className="coach-form-group">
            <label className="coach-label">Difficulty</label>
            <select
              className="coach-select"
              value={newTutForm.difficulty}
              onChange={(e) => setNewTutForm((f) => ({ ...f, difficulty: e.target.value }))}
            >
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div className="coach-form-group">
            <label className="coach-label">Tags (comma-separated)</label>
            <input
              className="coach-input"
              value={newTutForm.tags}
              onChange={(e) => setNewTutForm((f) => ({ ...f, tags: e.target.value }))}
              placeholder="coverage, reads, zone"
            />
          </div>
          {saveMsg && <div className="coach-error" style={{ color: saveMsg.includes('Error') ? undefined : 'var(--coach-success)' }}>{saveMsg}</div>}
          <button className="btn btn-primary" onClick={handleCreateTutorial} disabled={saving}>
            {saving ? 'Creating…' : 'Create Lesson'}
          </button>
        </div>
      </div>
    );
  }

  /* ── Existing tutorial editor ──────────────────────────────────────────── */
  return (
    <>
      {/* Meta editor */}
      <div className="coach-section">
        <div className="coach-section-header">
          <h2 className="coach-section-title">Edit Lesson</h2>
          <div className="flex-row">
            {tutorial.status !== 'published' && (
              <button className="btn btn-primary btn-sm" onClick={handlePublish} disabled={saving}>
                Publish
              </button>
            )}
            {tutorial.status === 'published' && (
              <span className="badge badge-published">Published</span>
            )}
            <button className="btn btn-outline" onClick={onBack}>← Back</button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div className="coach-form-group" style={{ gridColumn: '1/-1' }}>
            <label className="coach-label">Title</label>
            <input
              className="coach-input"
              value={tutorial.title}
              onChange={(e) => setTutorial((p) => ({ ...p, title: e.target.value }))}
            />
          </div>
          <div className="coach-form-group">
            <label className="coach-label">Difficulty</label>
            <select
              className="coach-select"
              value={tutorial.difficulty}
              onChange={(e) => setTutorial((p) => ({ ...p, difficulty: e.target.value }))}
            >
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div className="coach-form-group" style={{ gridColumn: '2/-1' }}>
            <label className="coach-label">Tags</label>
            <input
              className="coach-input"
              value={(tutorial.tags || []).join(', ')}
              onChange={(e) => setTutorial((p) => ({ ...p, tags: e.target.value.split(',').map((t) => t.trim()) }))}
            />
          </div>
        </div>
        {saveMsg && (
          <div style={{ color: saveMsg === 'Saved!' || saveMsg === 'Published!' ? 'var(--coach-success)' : 'var(--coach-danger)', fontSize: '0.875rem', marginBottom: 8 }}>
            {saveMsg}
          </div>
        )}
        <button className="btn btn-outline btn-sm" onClick={handleUpdateMeta} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {/* Segments */}
      <div className="coach-section">
        <div className="coach-section-header">
          <h2 className="coach-section-title">Segments ({(tutorial.segments || []).length})</h2>
        </div>

        {(tutorial.segments || []).length === 0 ? (
          <div className="coach-empty" style={{ padding: '16px 0' }}>
            <div className="coach-empty-desc">No segments yet. Add the first one below.</div>
          </div>
        ) : (
          <table className="coach-table" style={{ marginBottom: 20 }}>
            <thead>
              <tr>
                <th>#</th>
                <th>Title</th>
                <th>Type</th>
                <th>Engine</th>
                <th>Concept</th>
                <th>Content</th>
                <th>Order</th>
              </tr>
            </thead>
            <tbody>
              {(tutorial.segments || []).map((s, i) => {
                const linkedSceneId = s.scene_id || s.scenario_id;
                const typeDef = sceneTypes.find((t) => t.id === s.scene_type);
                const designerBase = typeDef?.designer_route || '/coach/scene-data';
                const segs = tutorial.segments || [];
                return (
                  <tr key={s.segment_id || i}>
                    <td className="text-muted text-sm">{i + 1}</td>
                    <td style={{ fontWeight: 600 }}>{s.title}</td>
                    <td><span className="badge badge-draft">{s.segment_type}</span></td>
                    <td className="text-muted text-sm">{s.authoring_engine}</td>
                    <td className="text-muted text-sm">{s.concept_id || '—'}</td>
                    <td>
                      {linkedSceneId ? (
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => navigate(`${designerBase}/${linkedSceneId}?domain=${tutorial.domain_id || ''}`)}
                          title={`Edit ${s.scene_type || 'scene'} ${linkedSceneId}`}
                        >
                          Edit content →
                        </button>
                      ) : (
                        <span className="text-muted text-sm">inline</span>
                      )}
                    </td>
                    <td>
                      <div className="flex-row" style={{ gap: 2 }}>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          disabled={i === 0}
                          title="Move up"
                          onClick={async () => {
                            const next = [...segs];
                            [next[i - 1], next[i]] = [next[i], next[i - 1]];
                            const ids = next.map((seg) => seg.segment_id);
                            try {
                              const { data } = await coachAPI.updateTutorial(tutorial.tutorial_id, { segment_ids: ids });
                              setTutorial(data);
                            } catch {}
                          }}
                          style={{ padding: '2px 6px', fontSize: '0.75rem' }}
                        >↑</button>
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          disabled={i === segs.length - 1}
                          title="Move down"
                          onClick={async () => {
                            const next = [...segs];
                            [next[i], next[i + 1]] = [next[i + 1], next[i]];
                            const ids = next.map((seg) => seg.segment_id);
                            try {
                              const { data } = await coachAPI.updateTutorial(tutorial.tutorial_id, { segment_ids: ids });
                              setTutorial(data);
                            } catch {}
                          }}
                          style={{ padding: '2px 6px', fontSize: '0.75rem' }}
                        >↓</button>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          title="Remove segment"
                          onClick={async () => {
                            const ok = await confirm({
                              title: 'Remove segment?',
                              message: `Remove "${s.title}" from this lesson?`,
                              confirmLabel: 'Remove',
                              danger: true,
                            });
                            if (!ok) return;
                            try {
                              await coachAPI.deleteSegment(tutorial.tutorial_id, s.segment_id);
                              setTutorial((prev) => ({
                                ...prev,
                                segments: (prev.segments || []).filter((seg) => seg.segment_id !== s.segment_id),
                              }));
                              toast.info('Segment removed');
                            } catch {}
                          }}
                          style={{ padding: '2px 6px', fontSize: '0.75rem' }}
                        >×</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Add segment form */}
        <div style={{
          background: 'rgba(79,142,247,0.04)',
          border: '1px dashed var(--coach-border)',
          borderRadius: 'var(--coach-radius)',
          padding: 18,
        }}>
          <div className="coach-label" style={{ marginBottom: 12 }}>Add Segment</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, alignItems: 'end' }}>
            <div className="coach-form-group" style={{ margin: 0 }}>
              <label className="coach-label">Type</label>
              <select
                className="coach-select"
                value={segForm.segment_type}
                onChange={(e) => {
                  const next = e.target.value;
                  setSegForm((f) => ({
                    ...f,
                    segment_type: next,
                    // Pre-pick a sensible engine when switching to scenario
                    authoring_engine: next === 'scenario' ? 'native_spatial' : f.authoring_engine,
                  }));
                }}
              >
                {/* Always include "scenario" + "summary" since they aren't
                    tied to a single scene_type; everything else comes from
                    the per-domain catalog's segment_type field so each
                    domain sees only the segment shapes its scenes
                    produce. */}
                <option value="scenario">Scenario (link to a scene)</option>
                {Array.from(
                  new Set(
                    (sceneTypes || [])
                      .map((t) => t.segment_type)
                      .filter((s) => s && s !== 'scenario'),
                  ),
                ).map((segType) => (
                  <option key={segType} value={segType}>
                    {segType.charAt(0).toUpperCase() + segType.slice(1).replace(/_/g, ' ')}
                  </option>
                ))}
                <option value="summary">Summary</option>
              </select>
            </div>
            <div className="coach-form-group" style={{ margin: 0 }}>
              <label className="coach-label">Title</label>
              <input
                className="coach-input"
                value={segForm.title}
                onChange={(e) => setSegForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Segment title"
              />
            </div>
            <div className="coach-form-group" style={{ margin: 0 }}>
              <label className="coach-label">Engine</label>
              <select
                className="coach-select"
                value={segForm.authoring_engine}
                onChange={(e) => setSegForm((f) => ({ ...f, authoring_engine: e.target.value }))}
              >
                <option value="native_video">Native Video</option>
                <option value="native_quiz">Native Quiz</option>
                <option value="native_spatial">Spatial Field</option>
                <option value="h5p">H5P</option>
              </select>
            </div>
            <div className="coach-form-group" style={{ margin: 0 }}>
              <label className="coach-label">Concept ID</label>
              <input
                className="coach-input"
                value={segForm.concept_id}
                onChange={(e) => setSegForm((f) => ({ ...f, concept_id: e.target.value }))}
                placeholder="cover_2 (optional)"
              />
            </div>
          </div>

          {/* Resource URL fields — only shown for curated_resource segments. */}
          {segForm.segment_type === 'curated_resource' && (
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginTop: 10 }}>
              <div className="coach-form-group" style={{ margin: 0 }}>
                <label className="coach-label">Resource URL <span style={{ color: 'var(--coach-danger)' }}>*</span></label>
                <input
                  className="coach-input"
                  type="url"
                  value={segForm.resource_url}
                  onChange={(e) => setSegForm((f) => ({ ...f, resource_url: e.target.value }))}
                  placeholder="https://example.com/article"
                />
              </div>
              <div className="coach-form-group" style={{ margin: 0 }}>
                <label className="coach-label">Source name</label>
                <input
                  className="coach-input"
                  value={segForm.source_name}
                  onChange={(e) => setSegForm((f) => ({ ...f, source_name: e.target.value }))}
                  placeholder="Coursera, arXiv, YouTube…"
                />
              </div>
              <div className="coach-form-group" style={{ margin: 0 }}>
                <label className="coach-label">Est. minutes</label>
                <input
                  className="coach-input"
                  type="number"
                  min="1"
                  value={segForm.estimated_minutes}
                  onChange={(e) => setSegForm((f) => ({ ...f, estimated_minutes: e.target.value }))}
                  placeholder="10"
                />
              </div>
            </div>
          )}

          {/* Prompt Canvas fields — only shown for prompt_canvas segments. */}
          {segForm.segment_type === 'prompt_canvas' && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, marginBottom: 10 }}>
                <div className="coach-form-group" style={{ margin: 0 }}>
                  <label className="coach-label">Topic Summary</label>
                  <input
                    className="coach-input"
                    value={segForm.topic_summary}
                    onChange={(e) => setSegForm((f) => ({ ...f, topic_summary: e.target.value }))}
                    placeholder="Brief description shown beneath the title"
                  />
                </div>
                <div className="coach-form-group" style={{ margin: 0 }}>
                  <label className="coach-label">Difficulty</label>
                  <select
                    className="coach-select"
                    value={segForm.difficulty}
                    onChange={(e) => setSegForm((f) => ({ ...f, difficulty: e.target.value }))}
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>
              <div className="coach-form-group" style={{ marginBottom: 10 }}>
                <label className="coach-label">Key Insight (optional)</label>
                <textarea
                  className="coach-textarea"
                  rows={2}
                  value={segForm.key_insight}
                  onChange={(e) => setSegForm((f) => ({ ...f, key_insight: e.target.value }))}
                  placeholder="A highlighted takeaway shown at the bottom of the canvas."
                />
              </div>
              <div className="coach-form-group" style={{ marginBottom: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--coach-label-color, #374151)' }}>
                  <input
                    type="checkbox"
                    checked={segForm.saveable}
                    onChange={(e) => setSegForm((f) => ({ ...f, saveable: e.target.checked }))}
                  />
                  Allow learners to save this canvas to their library
                </label>
              </div>

              {/* Dynamic steps builder */}
              <div style={{ border: '1px solid var(--coach-border)', borderRadius: 8, padding: 14, background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span className="coach-label" style={{ margin: 0 }}>
                    Steps <span style={{ color: 'var(--coach-danger)' }}>*</span>
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => setPromptCanvasSteps((prev) => [
                      ...prev,
                      { label: '', type: 'prompt', language: '', content: '', copy_enabled: true },
                    ])}
                  >
                    + Add Step
                  </button>
                </div>

                {promptCanvasSteps.length === 0 && (
                  <div className="text-muted text-sm">No steps yet. Click "+ Add Step" to add the first one.</div>
                )}

                {promptCanvasSteps.map((step, i) => (
                  <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 12, marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>Step {i + 1}</span>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => setPromptCanvasSteps((prev) => prev.filter((_, idx) => idx !== i))}
                      >
                        Remove
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 8 }}>
                      <div className="coach-form-group" style={{ margin: 0 }}>
                        <label className="coach-label">Label</label>
                        <input
                          className="coach-input"
                          value={step.label}
                          onChange={(e) => setPromptCanvasSteps((prev) => prev.map((s, idx) => idx === i ? { ...s, label: e.target.value } : s))}
                          placeholder="e.g. Step 1 — Define the router prompt"
                        />
                      </div>
                      <div className="coach-form-group" style={{ margin: 0 }}>
                        <label className="coach-label">Type</label>
                        <select
                          className="coach-select"
                          value={step.type}
                          onChange={(e) => setPromptCanvasSteps((prev) => prev.map((s, idx) => idx === i ? { ...s, type: e.target.value } : s))}
                        >
                          <option value="prompt">Prompt</option>
                          <option value="code">Code</option>
                          <option value="concept">Concept</option>
                        </select>
                      </div>
                    </div>
                    <div className="coach-form-group" style={{ marginBottom: 8 }}>
                      <label className="coach-label">Content</label>
                      <textarea
                        className="coach-textarea"
                        rows={4}
                        value={step.content}
                        onChange={(e) => setPromptCanvasSteps((prev) => prev.map((s, idx) => idx === i ? { ...s, content: e.target.value } : s))}
                        placeholder="The prompt, code snippet, or concept text for this step."
                      />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' }}>
                      <input
                        type="checkbox"
                        checked={step.copy_enabled}
                        onChange={(e) => setPromptCanvasSteps((prev) => prev.map((s, idx) => idx === i ? { ...s, copy_enabled: e.target.checked } : s))}
                      />
                      Show copy button
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Link existing scene — available for all segment types */}
          {segForm.segment_type !== 'scenario' && (
            <div style={{ marginTop: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#cbd5e1' }}>
                <input
                  type="checkbox"
                  checked={linkExisting}
                  onChange={(e) => setLinkExisting(e.target.checked)}
                />
                Link to an existing scene from the Content Library
              </label>
              {linkExisting && (
                <div className="coach-form-group" style={{ margin: '8px 0 0' }}>
                  <select
                    className="coach-select"
                    value={segForm.scenario_id}
                    onChange={(e) => setSegForm((f) => ({ ...f, scenario_id: e.target.value }))}
                  >
                    <option value="">
                      {scenesLoading ? 'Loading scenes…' : 'Pick a scene from the Library…'}
                    </option>
                    {sceneOptions
                      .filter((s) => {
                        if (segForm.segment_type === 'concept') return true;
                        const st = s.scene_type || '';
                        const seg = segForm.segment_type || '';
                        return st === seg || st.includes(seg) || seg.includes(st);
                      })
                      .map((s) => (
                        <option key={s.scene_id} value={s.scene_id}>
                          {(s.display_name || s.scene_id)}{s.scene_type ? ` · ${s.scene_type}` : ''}{s.status ? ` · ${s.status}` : ''}
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Scene picker + narration script — for scenario segments. */}
          {segForm.segment_type === 'scenario' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginTop: 10 }}>
              <div className="coach-form-group" style={{ margin: 0 }}>
                <label className="coach-label">Scene to embed</label>
                <select
                  className="coach-select"
                  value={segForm.scenario_id}
                  onChange={(e) => setSegForm((f) => ({ ...f, scenario_id: e.target.value }))}
                >
                  <option value="">
                    {scenesLoading ? 'Loading scenes…' : 'Pick a scene from the Library…'}
                  </option>
                  {sceneOptions.map((s) => (
                    <option key={s.scene_id} value={s.scene_id}>
                      {(s.display_name || s.scene_id)}{s.scene_type ? ` · ${s.scene_type}` : ''}{s.status ? ` · ${s.status}` : ''}
                    </option>
                  ))}
                </select>
                {!scenesLoading && sceneOptions.length === 0 && (
                  <p className="text-muted text-sm" style={{ marginTop: 4 }}>
                    No scenes in this domain yet — create one in the Content Library first.
                  </p>
                )}
              </div>
              <div className="coach-form-group" style={{ margin: 0 }}>
                <label className="coach-label">Lead-in script (optional)</label>
                <textarea
                  className="coach-textarea"
                  rows={2}
                  value={segForm.script_text}
                  onChange={(e) => setSegForm((f) => ({ ...f, script_text: e.target.value }))}
                  placeholder="Short text shown above the Play button to set up the scene."
                />
              </div>
            </div>
          )}

          <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={handleAddSegment} disabled={saving}>
            {saving ? 'Adding…' : '+ Add Segment'}
          </button>
        </div>
      </div>
    </>
  );
}

/* ── Page ────────────────────────────────────────────────────────────────── */

export default function TutorialBuilderPage() {
  const { tutorialId } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState(tutorialId ? 'editor' : 'list');
  const [editId, setEditId] = useState(tutorialId || null);

  const openNew = () => {
    setEditId('new');
    setView('editor');
    navigate('/coach/tutorials/new', { replace: true });
  };

  const openEdit = (id) => {
    setEditId(id);
    setView('editor');
    navigate(`/coach/tutorials/${id}`, { replace: true });
  };

  const goBack = () => {
    setView('list');
    setEditId(null);
    navigate('/coach/tutorials', { replace: true });
  };

  return (
    <CoachLayout title="Lesson Builder">
      {view === 'list' ? (
        <TutorialList onEdit={openEdit} onNew={openNew} />
      ) : (
        <TutorialEditor tutorialId={editId} onBack={goBack} />
      )}
    </CoachLayout>
  );
}
